// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { CopyOutlined, DownloadOutlined, ThunderboltOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { DistributionListRecipient } from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import type { DistributionListFilterOption } from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import type { DistributionListResolvePayload } from '../../../state_manager/interfaces/distribution_lists.ts'
import { useSendCommunication } from '../hooks/useSendCommunication.ts'
import { renderContactOption, contactOptionFilter } from './contactOptionRender.tsx'
// Shared resolve helpers — extracted to core/utils when Email Phase 3 grew
// a second consumer (the email-campaign Recipients tab). Same RFC-5322
// formatting + lowercased-email dedupe + accounts→contacts tree used here.
import {
  formatAddr,
  dedupeByEmail,
  contactName,
  pairKey,
  hasEmail,
  buildAccountTree,
  type AccountNode,
  type ContactNode,
  type ResolveGridRow as GridRow
} from '../../../core/utils/distributionListResolveUtils.ts'

const { Title, Text } = Typography

interface SendFormValues {
  list_id?: string
  topic_id?: string
  roles_limit: string[]
  funds_limit: string[]
  lp_status_limit: string[]
  accounts_limit: string[]
  accounts_add: string[]
  contacts_add: string[]
}

const EMPTY_FORM: SendFormValues = {
  list_id: undefined,
  topic_id: undefined,
  roles_limit: [],
  funds_limit: [],
  lp_status_limit: [],
  accounts_limit: [],
  accounts_add: [],
  contacts_add: []
}

export default function SendCommunicationPage() {
  const {
    savedLists,
    listsLoading,
    options,
    optionsLoading,
    recipients,
    resolving,
    loadListDefinition,
    resolve,
    clearRecipients
  } = useSendCommunication()
  const [form] = Form.useForm<SendFormValues>()

  // Session-only exclusion state (decision §2.6/§2.15d — affects ONLY this
  // send, never persisted). A contact is included iff it has an email AND its
  // account is not excluded AND it was not manually excluded.
  const [excludedAccounts, setExcludedAccounts] = useState<Set<string>>(new Set())
  const [excludedPairs, setExcludedPairs] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [listLoading, setListLoading] = useState(false)

  // Export controls (decision §2.15e). Separator default ';' (Outlook — the
  // org's Microsoft/Azure env); toggle to ',' for Gmail/Apple Mail.
  const [separator, setSeparator] = useState<';' | ','>(';')
  const [exportLayout, setExportLayout] = useState<'flat' | 'byAccount'>('flat')

  const watchedListId = Form.useWatch('list_id', form)
  const watchedTopicId = Form.useWatch('topic_id', form)

  const listOptions = useMemo(
    () =>
      savedLists.map((l) => ({
        value: l.distribution_lists__id,
        label: l.distribution_lists__name
      })),
    [savedLists]
  )
  const topicOptions = useMemo(() => options?.topic ?? [], [options])
  const roleOptions = useMemo(() => options?.roles ?? [], [options])
  const fundOptions = useMemo(() => options?.funds ?? [], [options])
  const lpStatusOptions = useMemo(() => options?.lp_status ?? [], [options])
  const accountOptions = useMemo(() => options?.accounts ?? [], [options])
  const contactOptions = useMemo(() => options?.contacts ?? [], [options])

  // Pick a saved list → pre-populate the editable filters AND seed
  // accounts_add/contacts_add from the list's persisted force-include overlay
  // (§2.13b). Usage-time amendments layer on top, session-only (§2.6).
  const onPickList = useCallback(
    async (listId: string | undefined) => {
      form.setFieldsValue({ list_id: listId })
      clearRecipients()
      if (!listId) return
      setListLoading(true)
      try {
        const d = await loadListDefinition(listId)
        if (!d) {
          showErrorToast('That distribution list could not be loaded (it may have been deleted).')
          return
        }
        form.setFieldsValue({
          topic_id: d.distribution_lists__topic_id,
          roles_limit: d.distribution_lists__role_ids ?? [],
          funds_limit: d.distribution_lists__fund_ids ?? [],
          lp_status_limit: d.distribution_lists__lp_status_ids ?? [],
          accounts_limit: [],
          accounts_add: d.distribution_lists__account_ids ?? [],
          contacts_add: d.distribution_lists__contact_ids ?? []
        })
      } finally {
        setListLoading(false)
      }
    },
    [form, loadListDefinition, clearRecipients]
  )

  const handleGenerate = useCallback(async () => {
    let values: SendFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    const payload: DistributionListResolvePayload = {
      topic_id: values.topic_id as string,
      roles_limit: values.roles_limit ?? [],
      funds_limit: values.funds_limit ?? [],
      accounts_limit: values.accounts_limit ?? [],
      lp_status_limit: values.lp_status_limit ?? [],
      accounts_add: values.accounts_add ?? [],
      contacts_add: values.contacts_add ?? []
    }
    const err = await resolve(payload)
    // Fresh resolution ⇒ reset the session-only exclusion + expand state.
    setExcludedAccounts(new Set())
    setExcludedPairs(new Set())
    setExpandedKeys([])
    if (err) showErrorToast(err)
  }, [form, resolve])

  // Group the flat (account, contact) rows into a collapsible
  // accounts → contacts tree (decision §2.15d). Shared builder, reused by
  // the Email Phase 3 Recipients tab.
  const tree: AccountNode[] = useMemo(() => buildAccountTree(recipients), [recipients])

  // Expand-all / collapse-all (controlled expandedRowKeys) — the same dual
  // +/- header control as Phase 3 Topic Subscriptions (plan §2.11c).
  const allAccountKeys = useMemo(() => tree.map((n) => n.key), [tree])
  const allExpanded = tree.length > 0 && expandedKeys.length >= tree.length
  const anyExpanded = expandedKeys.length > 0
  const expandAll = useCallback(() => setExpandedKeys(allAccountKeys), [allAccountKeys])
  const collapseAll = useCallback(() => setExpandedKeys([]), [])

  const isIncluded = useCallback(
    (r: DistributionListRecipient): boolean =>
      hasEmail(r) && !excludedAccounts.has(r.accounts__id) && !excludedPairs.has(pairKey(r)),
    [excludedAccounts, excludedPairs]
  )

  const exclusionReason = useCallback(
    (r: DistributionListRecipient): string | null => {
      if (!hasEmail(r)) return 'No email address'
      if (excludedAccounts.has(r.accounts__id)) return 'Account excluded'
      if (excludedPairs.has(pairKey(r))) return 'Manually excluded'
      return null
    },
    [excludedAccounts, excludedPairs]
  )

  const toggleAccount = useCallback((accountId: string, checked: boolean) => {
    setExcludedAccounts((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(accountId)
      else next.add(accountId)
      return next
    })
  }, [])

  const toggleContact = useCallback((r: DistributionListRecipient, checked: boolean) => {
    setExcludedPairs((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(pairKey(r))
      else next.add(pairKey(r))
      return next
    })
  }, [])

  const includedRecipients = useMemo(
    () => (recipients ?? []).filter(isIncluded),
    [recipients, isIncluded]
  )

  const summary = useMemo(() => {
    const total = recipients?.length ?? 0
    const included = includedRecipients.length
    return { total, included, excluded: total - included, accounts: tree.length }
  }, [recipients, includedRecipients, tree])

  // ── Export: email-client-ready recipient text (decision §2.15e) ──────────
  const sep = `${separator} `

  // A — flat, de-duplicated by email across all accounts: one separator-joined
  // block to paste straight into the Bcc field.
  const flatText = useMemo(
    () => dedupeByEmail(includedRecipients).map(formatAddr).join(sep),
    [includedRecipients, sep]
  )

  // B — grouped by account: account name heading, next line that account's
  // included recipients (de-duped by email within the account), blank line
  // between accounts.
  const perAccountText = useMemo(() => {
    const blocks: string[] = []
    for (const node of tree) {
      const incl = dedupeByEmail(node.contacts.filter(isIncluded))
      if (incl.length === 0) continue
      blocks.push(`${node.accountName}\n${incl.map(formatAddr).join(sep)}`)
    }
    return blocks.join('\n\n')
  }, [tree, isIncluded, sep])

  const currentText = exportLayout === 'flat' ? flatText : perAccountText

  const pickedListName = useMemo(
    () => savedLists.find((l) => l.distribution_lists__id === watchedListId)?.distribution_lists__name ?? '',
    [savedLists, watchedListId]
  )
  const topicLabel = useMemo(
    () => topicOptions.find((t) => t.value === watchedTopicId)?.label ?? '',
    [topicOptions, watchedTopicId]
  )

  const copyCurrent = useCallback(async () => {
    if (!currentText) {
      toast.info('No included recipients to copy.')
      return
    }
    try {
      await navigator.clipboard.writeText(currentText)
      toast.success(
        `Copied ${exportLayout === 'flat' ? 'flat list' : 'per-account list'} to clipboard`
      )
    } catch {
      showErrorToast('Clipboard blocked by the browser — use Download .txt instead.')
    }
  }, [currentText, exportLayout])

  const downloadTxt = useCallback(() => {
    if (summary.included === 0) {
      toast.info('No included recipients to export.')
      return
    }
    const stamp = new Date().toISOString().slice(0, 10)
    const head = [
      'Send Communication — recipient export',
      pickedListName ? `Distribution list: ${pickedListName}` : null,
      topicLabel ? `Topic: ${topicLabel}` : null,
      `Generated: ${new Date().toISOString()}`,
      `Separator: "${separator}"  ·  ${summary.included} recipient(s), de-duplicated by email`,
      'Session-only — nothing was saved to subscriptions.'
    ]
      .filter(Boolean)
      .join('\n')
    const body =
      `${head}\n\n` +
      `===== Flat (de-duplicated) =====\n${flatText}\n\n` +
      `===== By account =====\n${perAccountText}\n`
    // Same browser Blob download idiom utilitiesService uses (no backend
    // round-trip), text/plain (decision §2.15e).
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `send-communication-recipients-${stamp}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [summary.included, pickedListName, topicLabel, separator, flatText, perAccountText])

  const accountCheckState = useCallback(
    (node: AccountNode): { checked: boolean; indeterminate: boolean; sendable: number } => {
      const sendableContacts = node.contacts.filter(hasEmail)
      const includedCount = sendableContacts.filter(isIncluded).length
      const checked = !excludedAccounts.has(node.accountId)
      const indeterminate =
        checked && sendableContacts.length > 0 && includedCount > 0 && includedCount < sendableContacts.length
      return { checked, indeterminate, sendable: sendableContacts.length }
    },
    [excludedAccounts, isIncluded]
  )

  const columns: ColumnsType<GridRow> = useMemo(
    () => [
      {
        title: (
          <div className='flex items-center gap-2'>
            <Space size={2}>
              <Tooltip title='Expand all'>
                <Button
                  type='text'
                  size='small'
                  aria-label='Expand all'
                  onClick={expandAll}
                  disabled={tree.length === 0 || allExpanded}
                  style={{ padding: 0, width: 22, height: 22, lineHeight: '20px', fontWeight: 700 }}
                >
                  +
                </Button>
              </Tooltip>
              <Tooltip title='Collapse all'>
                <Button
                  type='text'
                  size='small'
                  aria-label='Collapse all'
                  onClick={collapseAll}
                  disabled={!anyExpanded}
                  style={{ padding: 0, width: 22, height: 22, lineHeight: '18px', fontWeight: 700 }}
                >
                  −
                </Button>
              </Tooltip>
            </Space>
            <span>Recipient</span>
          </div>
        ),
        key: 'recipient',
        render: (_: unknown, row: GridRow) => {
          if (row.isAccount) {
            const n = row as AccountNode
            return (
              <Space>
                <Text strong>{n.accountName}</Text>
                <Tag color='geekblue'>
                  {n.contacts.length} contact{n.contacts.length === 1 ? '' : 's'}
                </Tag>
              </Space>
            )
          }
          const r = (row as ContactNode).recipient
          return <span>{contactName(r)}</span>
        }
      },
      {
        title: 'Email',
        key: 'email',
        width: 280,
        render: (_: unknown, row: GridRow) =>
          row.isAccount ? null : (
            (row as ContactNode).recipient.contacts__email || (
              <Text type='secondary'>—</Text>
            )
          )
      },
      {
        title: 'Status',
        key: 'status',
        width: 180,
        render: (_: unknown, row: GridRow) => {
          if (row.isAccount) {
            const n = row as AccountNode
            const inc = n.contacts.filter(isIncluded).length
            return (
              <Text type='secondary'>
                {inc}/{n.contacts.length} included
              </Text>
            )
          }
          const r = (row as ContactNode).recipient
          const reason = exclusionReason(r)
          return reason ? (
            <Tag color={reason === 'No email address' ? 'red' : 'orange'}>{reason}</Tag>
          ) : (
            <Tag color='green'>Included</Tag>
          )
        }
      },
      {
        title: 'Include',
        key: 'include',
        width: 90,
        align: 'center',
        render: (_: unknown, row: GridRow) => {
          if (row.isAccount) {
            const n = row as AccountNode
            const { checked, indeterminate, sendable } = accountCheckState(n)
            return (
              <Tooltip title={checked ? 'Exclude this account' : 'Include this account'}>
                <Checkbox
                  checked={checked}
                  indeterminate={indeterminate}
                  disabled={sendable === 0}
                  onChange={(e) => toggleAccount(n.accountId, e.target.checked)}
                />
              </Tooltip>
            )
          }
          const r = (row as ContactNode).recipient
          const noEmail = !hasEmail(r)
          const accountExcluded = excludedAccounts.has(r.accounts__id)
          return (
            <Tooltip
              title={
                noEmail
                  ? 'No email address — cannot be sent'
                  : accountExcluded
                    ? 'Its account is excluded'
                    : ''
              }
            >
              <Checkbox
                checked={isIncluded(r)}
                disabled={noEmail || accountExcluded}
                onChange={(e) => toggleContact(r, e.target.checked)}
              />
            </Tooltip>
          )
        }
      }
    ],
    [
      accountCheckState,
      exclusionReason,
      excludedAccounts,
      isIncluded,
      toggleAccount,
      toggleContact,
      expandAll,
      collapseAll,
      allExpanded,
      anyExpanded,
      tree.length
    ]
  )

  return (
    <div className='h-full overflow-auto bg-white'>
      <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
        <Title level={4} style={{ marginTop: 0 }}>
          Send Communication
        </Title>

        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message='Usage-time only'
          description='Pick a saved distribution list, amend the filters for this send if needed, then Generate to resolve recipients. Include/exclude toggles affect only this send — they never change standing topic subscriptions. Until automated send exists, copy the email-client-ready recipient text (or download it as .txt) and paste it into your mail client.'
        />

        <Form<SendFormValues> form={form} layout='vertical' initialValues={EMPTY_FORM} autoComplete='off'>
          <Card title='Distribution list' style={{ marginBottom: 16 }}>
            <Form.Item
              name='list_id'
              label='Saved list'
              tooltip='Pre-populates the editable filters below from the list definition (incl. its always-include accounts/contacts).'
            >
              <Select
                allowClear
                showSearch
                placeholder='Select a saved distribution list'
                loading={listsLoading || listLoading}
                options={listOptions}
                optionFilterProp='label'
                onChange={(v) => onPickList(v as string | undefined)}
              />
            </Form.Item>
          </Card>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card title='Filters for this send' style={{ marginBottom: 16 }}>
                <Form.Item
                  name='topic_id'
                  label='Topic'
                  rules={[{ required: true, message: 'A topic is required to resolve recipients' }]}
                  tooltip='Recipients are resolved from accounts/contacts subscribed to this topic.'
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder='Select a topic'
                    loading={optionsLoading}
                    options={topicOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item name='roles_limit' label='Account roles' tooltip='Empty = no account-role filter.'>
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Any account role'
                    options={roleOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item name='funds_limit' label='Funds' tooltip='Empty = no fund filter.'>
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Any fund'
                    options={fundOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item name='lp_status_limit' label='LP statuses' tooltip='Empty = no LP-status filter.'>
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Any LP status'
                    options={lpStatusOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title='Usage-time overrides (this send only)' style={{ marginBottom: 16 }}>
                <Form.Item
                  name='accounts_limit'
                  label='Limit to accounts'
                  tooltip='Optional. Narrows the resolution to only these accounts. Empty = no narrowing.'
                >
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Not limited'
                    options={accountOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item
                  name='accounts_add'
                  label='Also include accounts'
                  tooltip='Force-included on top of the resolution. Seeded from the saved list, then editable for this send.'
                >
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='No extra accounts'
                    options={accountOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item
                  name='contacts_add'
                  label='Also include contacts'
                  tooltip='Force-included on top of the resolution. Seeded from the saved list, then editable for this send. Search by name or email.'
                >
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='No extra contacts'
                    options={contactOptions}
                    optionRender={(oi) => renderContactOption(oi.data as DistributionListFilterOption)}
                    filterOption={(input, opt) =>
                      contactOptionFilter(input, opt as DistributionListFilterOption | undefined)
                    }
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 16 }}>
            <Button
              type='primary'
              icon={<ThunderboltOutlined />}
              loading={resolving}
              onClick={handleGenerate}
            >
              Generate recipients
            </Button>
          </Space>
        </Form>

        {recipients !== null && (
          <>
            <Card
              style={{ marginBottom: 16 }}
              title={
                <Space size='large' wrap>
                  <span>Recipients</span>
                  <Text type='secondary'>
                    {summary.accounts} account{summary.accounts === 1 ? '' : 's'} ·{' '}
                    {summary.included} included · {summary.excluded} excluded · {summary.total} total
                  </Text>
                </Space>
              }
            >
              {summary.total === 0 ? (
                <Empty description='No recipients resolved for these filters.' />
              ) : (
                <Table<GridRow>
                  rowKey='key'
                  columns={columns}
                  dataSource={tree}
                  pagination={false}
                  size='small'
                  expandable={{
                    expandedRowKeys: expandedKeys,
                    onExpandedRowsChange: (keys) => setExpandedKeys(keys as string[])
                  }}
                  scroll={{ y: 480 }}
                />
              )}
            </Card>

            {summary.included > 0 && (
              <Card title='Export — email-client-ready recipients'>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Radio.Group
                    value={exportLayout}
                    onChange={(e) => setExportLayout(e.target.value)}
                    optionType='button'
                    buttonStyle='solid'
                    options={[
                      { label: 'Flat (de-duplicated)', value: 'flat' },
                      { label: 'By account', value: 'byAccount' }
                    ]}
                  />
                  <Radio.Group
                    value={separator}
                    onChange={(e) => setSeparator(e.target.value)}
                    optionType='button'
                    options={[
                      { label: '; Outlook', value: ';' },
                      { label: ', Gmail / Apple', value: ',' }
                    ]}
                  />
                  <Button icon={<CopyOutlined />} onClick={copyCurrent}>
                    Copy {exportLayout === 'flat' ? 'flat list' : 'per-account list'}
                  </Button>
                  <Button icon={<DownloadOutlined />} onClick={downloadTxt}>
                    Download .txt
                  </Button>
                </Space>
                <Input.TextArea
                  readOnly
                  value={currentText}
                  autoSize={{ minRows: 6, maxRows: 18 }}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                <Text type='secondary' style={{ display: 'block', marginTop: 8 }}>
                  {summary.included} recipient(s), de-duplicated by email. Paste into your mail
                  client. Nothing here is saved — this is a one-off send composition.
                </Text>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}
